import type { i18n as I18n, PostProcessorModule } from 'i18next';

/**
 * R3-B16 — the per-string machine mark (#2109 R3, lane L9; PRD F-16).
 *
 * An i18next post-processor appends a small superscript "MT" to a resolved
 * string whose key state is `machine` or `draft`, so a TRF reviewer walking a
 * page in Spanish can see which sentences no person has signed off yet. It is
 * the per-string half of the honesty rule; the R1 banner is the page half.
 *
 * - NON-PRODUCTION ONLY. `loadMachineMark` returns before it imports anything
 *   when `import.meta.env.PROD` is true, so the production bundle never carries
 *   the per-key artifact (`review-state.keys.generated.json`, written by
 *   `scripts/i18n-review-state.mjs`). `machineMark.build.test.ts` bundles this
 *   file both ways and proves it.
 * - NON-ENGLISH ONLY. English is the source; it is never machine output.
 * - ONLY TEXT THAT CAME FROM THE TARGET CATALOG. An untranslated key falls back
 *   to English under the banner, and English is not a machine translation, so
 *   it is not marked. The processor reads the language i18next actually
 *   resolved from (`postProcessPassResolved`).
 * - NEVER CHAT-CORE. `chat:core.*` reaches message bubbles and the voice PiP,
 *   which would read the glyph as content.
 * - The glyph is not the `(?)` pending-decision marker, so a reviewer never
 *   confuses "TRF has not ruled on this" with "no person has checked this
 *   translation". `(?)` marker `machine-mark-glyph` (PM, on the language row).
 */

/** U+1D39 U+1D40, MODIFIER LETTER CAPITAL M and T: a superscript "MT". */
export const MACHINE_MARK = 'ᴹᵀ';
export const MACHINE_MARK_NAME = 'trfMachineMark';

/** `{ [code]: { [namespace]: { [key]: state } } }` — the per-key artifact. */
export type KeyStates = Record<string, Record<string, Record<string, string>>>;

const UNSIGNED = new Set(['machine', 'draft']);
const EXEMPT_PREFIXES = ['chat:core.'];

const isEnglish = (code: string | undefined) => !code || code === 'en' || code.startsWith('en-');

/**
 * Should `ns:key`, resolved from `code`, carry the mark? A key the artifact
 * does not list has no state entry at all, which the derivation calls `draft`.
 */
export function shouldMark(states: KeyStates, code: string | undefined, ns: string, key: string): boolean {
  if (isEnglish(code) || !code) return false;
  const full = `${ns}:${key}`;
  if (EXEMPT_PREFIXES.some((p) => full.startsWith(p))) return false;
  const state = states[code]?.[ns]?.[key];
  return state === undefined || UNSIGNED.has(state);
}

interface Resolved {
  usedLng?: string;
  usedNS?: string;
  usedKey?: string;
  /** The key with its plural suffix (`item_one`), where `usedKey` is the base. */
  exactUsedKey?: string;
}

export function createMachineMark(states: KeyStates): PostProcessorModule {
  return {
    type: 'postProcessor',
    name: MACHINE_MARK_NAME,
    process(value: string, _key: string | string[], options: { i18nResolved?: Resolved }) {
      const resolved = options?.i18nResolved;
      const key = resolved?.exactUsedKey || resolved?.usedKey;
      if (typeof value !== 'string' || value === '' || !resolved?.usedLng || !resolved.usedNS || !key) {
        return value;
      }
      return shouldMark(states, resolved.usedLng, resolved.usedNS, key) ? `${value}${MACHINE_MARK}` : value;
    },
  };
}

let installed: KeyStates | null = null;

/**
 * Register the processor on a live instance. i18next reads `postProcess` and
 * `postProcessPassResolved` from the options object its translator shares, so
 * this works after `init`. Returns an uninstall function (tests).
 */
export function installMachineMark(i18n: I18n, states: KeyStates): () => void {
  const options = i18n.options as { postProcess?: string | string[] | false; postProcessPassResolved?: boolean };
  const before = { postProcess: options.postProcess, passResolved: options.postProcessPassResolved };
  const current = typeof options.postProcess === 'string' ? [options.postProcess] : options.postProcess || [];
  i18n.use(createMachineMark(states));
  options.postProcess = current.includes(MACHINE_MARK_NAME) ? current : [...current, MACHINE_MARK_NAME];
  options.postProcessPassResolved = true;
  installed = states;
  return () => {
    options.postProcess = before.postProcess;
    options.postProcessPassResolved = before.passResolved;
    installed = null;
  };
}

/**
 * The same mark for a string that does not go through `t()` — the copy store
 * reads its catalog directly (R3-B17). Empty outside a non-production build.
 */
export function machineMarkFor(code: string | undefined, ns: string, key: string): string {
  return installed && shouldMark(installed, code, ns, key) ? MACHINE_MARK : '';
}

/**
 * The one entry point the app calls. In a production build the guard is a
 * constant `true`, the bundler drops the import below with the dead branch,
 * and the per-key artifact never reaches the bundle.
 */
export function loadMachineMark(i18n: I18n): Promise<boolean> {
  // The import sits INSIDE the non-production branch, not after an early
  // return: a bundler drops an `if (false)` block before it scans the block's
  // imports, but it may still bundle an import that merely follows a return.
  if (!import.meta.env.PROD) {
    return import('./review-state.keys.generated.json').then(({ default: states }) => {
      installMachineMark(i18n, states as KeyStates);
      return true;
    });
  }
  return Promise.resolve(false);
}
